import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, AlertTriangle } from "lucide-react";

const FIELDS = [["name", "Doc name", true, /name|title/i], ["url", "Google Doc link", true, /link|url|google/i], ["client", "Client", false, /client|company|brand|account/i],
  ["category", "Doc category", false, /categor|type/i], ["approved", "Approved date", false, /approv|date/i]];
const NONE = "__none";
const L = ({ children }) => <label className="text-[10px] text-[#9FA1A4] mb-1 block font-mono-data uppercase tracking-wide">{children}</label>;

function autoMap(headers) {
  const used = new Set(), m = {};
  FIELDS.forEach(([k, , , re]) => { const i = headers.findIndex((h, j) => !used.has(j) && re.test(h)); if (i >= 0) { used.add(i); m[k] = String(i); } });
  return m;
}

function Preview({ rows }) {
  return (
    <div className="max-h-72 overflow-y-auto rounded-2xl border border-[#EEEFEF]" data-testid="gdoc-import-preview">
      <table className="w-full text-xs"><tbody>
        {rows.map((r) => (
          <tr key={r.row} data-testid={r.ok ? "gdoc-preview-ready" : "gdoc-preview-skip"} className={`border-b border-[#f5f5f8] last:border-0 ${r.ok ? "bg-[#F3FBF6]" : "bg-amber-50/60"}`}>
            <td className="px-3 py-2 font-mono-data text-[10px] text-[#9FA1A4] w-10">{r.row}</td>
            <td className="px-3 py-2 text-[#06060C] max-w-[200px] truncate">{r.name || "—"}</td>
            <td className="px-3 py-2 text-[#595D62] truncate max-w-[120px]">{r.client || "—"}</td>
            <td className="px-3 py-2 text-[#595D62]">{r.category || (r.category_unmatched ? <span className="text-amber-700">no match</span> : "—")}</td>
            <td className="px-3 py-2 text-[#595D62] whitespace-nowrap">{r.approved_date || (r.date_unreadable ? <span className="text-amber-700">unreadable</span> : "—")}</td>
            <td className="px-3 py-2 whitespace-nowrap">{r.ok ? <span className="inline-flex items-center gap-1 text-[#1f9d55]"><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span> : <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> {r.reason}</span>}</td>
          </tr>
        ))}
      </tbody></table>
    </div>
  );
}

export function GoogleDocsImportDialog({ open, onClose, onDone }) {
  const [clients, setClients] = useState([]);
  const [wid, setWid] = useState("");
  const [sheet, setSheet] = useState(null);
  const [map, setMap] = useState({});
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSheet(null); setPv(null); setMap({}); setWid(""); api.get("/console/clients").then(({ data }) => setClients(data.clients || [])); } }, [open]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/console/docs/google-import/parse", fd); setSheet({ ...data, file: file.name }); setMap(autoMap(data.headers)); setPv(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const rows = () => sheet.rows.map((r) => Object.fromEntries(FIELDS.map(([k]) => [k, map[k] != null && map[k] !== NONE ? r[Number(map[k])] : ""])));
  const body = () => ({ rows: rows(), default_workspace_id: map.client && map.client !== NONE ? null : wid });
  const needClient = !(map.client && map.client !== NONE);
  const canPreview = sheet && map.name && map.name !== NONE && map.url && map.url !== NONE && (!needClient || wid);
  const preview = async () => { setBusy(true); try { const { data } = await api.post("/console/docs/google-import/preview", body()); setPv(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const commit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/console/docs/google-import/commit", body()); toast.success(`Imported ${data.imported} doc${data.imported === 1 ? "" : "s"}${data.skipped ? `, skipped ${data.skipped}` : ""}`); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-[#EEEFEF] sm:max-w-3xl" data-testid="gdoc-import-dialog">
        <DialogHeader><DialogTitle className="font-display">Import Google Docs</DialogTitle><DialogDescription>Bring in docs already approved in Google Docs. They show as Approved and open in Google.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <label className="flex items-center gap-3 rounded-2xl border border-dashed border-[#d9ccff] bg-[#FAF8FF] px-4 py-3 cursor-pointer hover:border-[#C9B8FF]" data-testid="gdoc-import-upload">
            {busy && !sheet ? <Loader2 className="h-4 w-4 animate-spin text-[#723FFF]" /> : <Upload className="h-4 w-4 text-[#723FFF]" />}
            <span className="text-sm text-[#06060C]">{sheet ? `${sheet.file} · ${sheet.rows.length} rows` : "Upload a CSV or Excel (.xlsx) file"}</span>
            <span className="ml-auto text-xs text-[#5b2fd6]">{sheet ? "Replace" : "Choose file"}</span>
            <input data-testid="gdoc-import-file" type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {sheet && <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {FIELDS.map(([k, l, req]) => (
              <div key={k}><L>{l}{req ? " *" : ""}</L>
                <Select value={map[k] ?? NONE} onValueChange={(v) => { setMap({ ...map, [k]: v }); setPv(null); }}>
                  <SelectTrigger data-testid={`gdoc-map-${k}`} className="bg-[#F6F6FD] border-[#EEEFEF] h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-[#EEEFEF] max-h-64"><SelectItem value={NONE}>{req ? "Pick a column" : "Not in sheet"}</SelectItem>{sheet.headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
            {needClient && <div><L>Client for every row *</L>
              <Select value={wid || NONE} onValueChange={(v) => { setWid(v === NONE ? "" : v); setPv(null); }}>
                <SelectTrigger data-testid="gdoc-default-client" className="bg-[#F6F6FD] border-[#EEEFEF] h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#EEEFEF] max-h-64"><SelectItem value={NONE}>Pick a client</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>}
          </div>}
          {pv && <><Preview rows={pv.rows} /><p className="text-xs text-[#595D62]" data-testid="gdoc-import-counts"><b className="text-[#1f9d55]">{pv.ready} ready</b> · <b className="text-amber-700">{pv.skipped} will be skipped</b></p></>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} className="rounded-full">Cancel</Button>
            {!pv ? <Button data-testid="gdoc-import-preview-btn" onClick={preview} disabled={busy || !canPreview} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview"}</Button>
              : <Button data-testid="gdoc-import-commit" onClick={commit} disabled={busy || !pv.ready} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${pv.ready} doc${pv.ready === 1 ? "" : "s"}`}</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
